import { useSyncExternalStore } from "react";

/**
 * True when the OS is asking for dark. Read by the Google button, which is
 * drawn by Google's own script and therefore cannot be themed in CSS the way
 * every other surface in this app is.
 * Also read by the create pool contest type icons (ISSUE-1092), whose badge
 * paint comes from `@sportsmet/theme`'s contestShades, a per-mode table
 * rather than a CSS variable.
 *
 * Light and dark follow the device's prefers-color-scheme only: there is no
 * in-app toggle and nothing is persisted.
 *
 * `src/test/dom.ts` pins `matchMedia().matches` to `false` and makes
 * `addEventListener` a no-op, so this hook reads `false` in every colocated
 * test unless that test replaces `matchMedia` itself. Dark mode's appearance is
 * verified in a real browser, not from a unit test.
 */

const PREFERS_DARK_QUERY = "(prefers-color-scheme: dark)";

function hasMatchMedia(): boolean {
  return (
    typeof window !== "undefined" && typeof window.matchMedia === "function"
  );
}

function subscribe(callback: () => void): () => void {
  if (!hasMatchMedia()) {
    return () => {
      // Nothing was subscribed to, so there is nothing to unsubscribe from.
    };
  }
  const mediaQueryList = window.matchMedia(PREFERS_DARK_QUERY);
  mediaQueryList.addEventListener("change", callback);
  return () => {
    mediaQueryList.removeEventListener("change", callback);
  };
}

function getSnapshot(): boolean {
  if (!hasMatchMedia()) {
    return false;
  }
  return window.matchMedia(PREFERS_DARK_QUERY).matches;
}

// React requires a server snapshot whenever the first two arguments touch
// `window`. This is a client-rendered Vite app with no server render, but light
// is the honest default for a surface that has not measured anything yet.
function getServerSnapshot(): boolean {
  return false;
}

export function useSystemDark(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
